/**
 * data/repos/reportsRepo — repositório da área `reports` e `goals` (Req 16, Req 17).
 *
 * Em mock: dados derivados de `opportunities` e `partners` para funil/ranking.
 * Em produção: POST /v1/reports/{type}/export para exportação CSV.
 *
 * Shapes reais (confirmados via curl):
 *   GET /reports/funnel
 *     → { stages: [{ stageId, stageName, category, count, totalCents, weightedForecastCents, currency }] }
 *   GET /reports/forecast?from=YYYY-MM-DD&to=YYYY-MM-DD
 *     → { rows: [{ buId, buName, year, month, weightedForecastCents, realizedCents, goalCents, currency }] }
 *   GET /reports/ranking
 *     → { rows: [{ ownerId, displayName, wonCount, wonValueCents, pipelineForecastCents, currency }] }
 *
 * Nota: o ApiClient já prefixa BASE_URL "/v1" — os paths aqui NÃO devem incluir "/v1".
 */

import { apiClient } from "../../services/ApiClient.js";
import { resolveSource } from "../source.js";
import type {
  ReportsData,
  FunnelReportRow,
  RankingRow,
  ForecastReport,
  FunnelByAccountRow,
  FunnelByAccountOpportunity,
  ForecastYearNode,
  ForecastQuarterNode,
  ForecastMonthNode,
} from "../../features/reports/types.js";
import type { Money } from "../../shared/money/money.js";
import { aggregateByCurrency } from "../../shared/money/money.js";

/** Moedas canônicas aceitas pelo portal (DD-008, ADR-0008). */
const VALID_CURRENCIES: ReadonlySet<string> = new Set(["BRL", "USD", "EUR"]);

/**
 * Converte uma string de moeda do backend para o tipo `Money['currency']`.
 *
 * Valida contra a lista canônica BRL/USD/EUR antes do cast para evitar que
 * strings inválidas ('GBP', 'brl', 'BRL ') poluam aggregateByCurrency e
 * formatMoney com chaves fora do contrato (PBT-02).
 * Strings não reconhecidas fazem fallback para 'BRL' (moeda padrão do tenant)
 * e são logadas em console.warn para rastreamento em desenvolvimento.
 */
function toCurrency(value: string | null | undefined): Money["currency"] {
  const trimmed = value?.trim() ?? "";
  if (VALID_CURRENCIES.has(trimmed)) {
    return trimmed as Money["currency"];
  }
  if (trimmed !== "") {
    // Moeda desconhecida vinda do backend — fallback defensivo (não quebrar a UI).
    console.warn(`[reportsRepo] moeda desconhecida: "${trimmed}" — usando BRL como fallback`);
  }
  return "BRL";
}

// ---------------------------------------------------------------------------
// DTOs da API real
// ---------------------------------------------------------------------------

/** DTO de uma linha do funil (GET /reports/funnel). */
interface FunnelStageDto {
  readonly stageId: string;
  readonly stageName: string;
  readonly category?: string | null;
  readonly count: number;
  readonly totalCents: number;
  readonly weightedForecastCents: number;
  readonly currency: string;
}

/** Wrapper do endpoint GET /reports/funnel. */
interface FunnelApiResponse {
  readonly stages: readonly FunnelStageDto[];
}

/** DTO de uma linha do forecast (GET /reports/forecast). */
interface ForecastRowDto {
  readonly buId: string;
  /** buName pode vir ausente ou vazio — exibir "—" nesses casos. */
  readonly buName?: string | null;
  readonly year: number;
  readonly month: number;
  readonly weightedForecastCents: number;
  readonly realizedCents: number;
  readonly goalCents: number;
  readonly currency: string;
}

/** Wrapper do endpoint GET /reports/forecast. */
interface ForecastApiResponse {
  readonly rows: readonly ForecastRowDto[];
}

/** DTO de uma linha do ranking (GET /reports/ranking). */
interface RankingRowDto {
  readonly ownerId: string;
  /** displayName pode vir ausente ou vazio — exibir "—" nesses casos. */
  readonly displayName?: string | null;
  readonly wonCount: number;
  readonly wonValueCents: number;
  readonly pipelineForecastCents: number;
  readonly currency: string;
}

/** Wrapper do endpoint GET /reports/ranking. */
interface RankingApiResponse {
  readonly rows: readonly RankingRowDto[];
}

// ---------------------------------------------------------------------------
// Mapeamento DTOs → tipos do portal
// ---------------------------------------------------------------------------

/**
 * Mapeia um FunnelStageDto para FunnelReportRow.
 * `stage` ← stageName (fallback: stageId).
 * `totalValue` ← totalCents + currency (centavos inteiros, conforme RNF 11).
 */
function mapFunnelStage(dto: FunnelStageDto): FunnelReportRow {
  return {
    stage: dto.stageName || dto.stageId,
    count: dto.count,
    totalValue: {
      amountCents: dto.totalCents,
      currency: toCurrency(dto.currency),
    },
  };
}

/**
 * Mapeia os rows do forecast para ForecastReport.
 *
 * O backend retorna linhas por BU/mês; o portal modela o forecast como
 * agregação de metas/realizados por moeda (MoneyByCurrency) + byRep (por
 * representante). Como o endpoint /forecast não retorna dados por representante,
 * byRep é intencionalmente vazio — será preenchido quando o backend oferecer
 * o breakdown por owner.
 *
 * `period` é derivado do range from/to passado à chamada (ex.: "2026").
 */
function mapForecastRows(rows: readonly ForecastRowDto[], year: number): ForecastReport {
  // Agrega weightedForecastCents por moeda (sem misturar — PBT-02).
  const forecastItems: Money[] = rows.map((r) => ({
    amountCents: r.weightedForecastCents,
    currency: toCurrency(r.currency),
  }));

  return {
    period: String(year),
    byRep: [], // endpoint atual não retorna breakdown por representante
    totalByCurrency: aggregateByCurrency(forecastItems),
  };
}

/**
 * Mapeia um RankingRowDto para RankingRow.
 * `repName` ← displayName; quando ausente/vazio → "—".
 * `won` ← wonValueCents + currency (centavos inteiros).
 */
function mapRankingRow(dto: RankingRowDto, index: number): RankingRow {
  return {
    rank: index + 1,
    repId: dto.ownerId,
    repName: dto.displayName?.trim() || "—",
    won: {
      amountCents: dto.wonValueCents,
      currency: toCurrency(dto.currency),
    },
    deals: dto.wonCount,
  };
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

async function fetchReportsMock(): Promise<ReportsData> {
  const { opps, owners } = await import("../mock.js");

  // Funil: agrega oportunidades por estágio
  const stageMap = new Map<string, { count: number; cents: number }>();
  for (const opp of opps) {
    const entry = stageMap.get(opp.stage) ?? { count: 0, cents: 0 };
    entry.count += 1;
    entry.cents += opp.value * 100; // mock value está em reais
    stageMap.set(opp.stage, entry);
  }

  const funnel: FunnelReportRow[] = [...stageMap.entries()].map(([stage, data]) => ({
    stage,
    count: data.count,
    totalValue: { amountCents: data.cents, currency: "BRL" } as Money,
  }));

  // Ranking: agrega ganho por responsável (stage === "won")
  const wonMap = new Map<string, { cents: number; deals: number }>();
  for (const opp of opps.filter((o) => o.stage === "won" || o.prob >= 70)) {
    const entry = wonMap.get(opp.owner) ?? { cents: 0, deals: 0 };
    entry.cents += opp.value * 100;
    entry.deals += 1;
    wonMap.set(opp.owner, entry);
  }

  const ranking: RankingRow[] = [...wonMap.entries()]
    .sort((a, b) => b[1].cents - a[1].cents)
    .map(([repId, data], i) => ({
      rank: i + 1,
      repId,
      repName: owners[repId]?.name ?? repId,
      won: { amountCents: data.cents, currency: "BRL" } as Money,
      deals: data.deals,
    }));

  // Forecast (multimoeda: inclui um item USD para exercitar PBT-02)
  const forecastItems: Money[] = [
    { amountCents: 87_486_000, currency: "BRL" },
    { amountCents: 120_000_00, currency: "USD" },
    { amountCents: 87_486_000, currency: "BRL" },
  ];

  const forecast: ForecastReport = {
    period: "Q3 2026",
    byRep: Object.entries(owners).map(([id, owner]) => ({
      repId: id,
      repName: owner.name,
      forecast: { amountCents: 50_000_00, currency: "BRL" },
      won:      { amountCents: 20_000_00, currency: "BRL" },
    })),
    totalByCurrency: aggregateByCurrency(forecastItems),
  };

  return { funnel, forecast, ranking };
}

// ---------------------------------------------------------------------------
// API (área promovida)
// ---------------------------------------------------------------------------

/**
 * Deriva o range from/to para o forecast com base no ano corrente.
 * O endpoint retorna 400 se os parâmetros não forem informados.
 *
 * Exemplo: para 2026 → from=2026-01-01, to=2026-12-31.
 */
function currentYearForecastRange(): { from: string; to: string; year: number } {
  const year = new Date().getFullYear();
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
    year,
  };
}

async function fetchReportsApi(range?: ReportRange, buId?: string | null): Promise<ReportsData> {
  const defaultRange = currentYearForecastRange();
  const from = range?.from ?? defaultRange.from;
  const to = range?.to ?? defaultRange.to;
  const year = range ? new Date(range.from).getFullYear() : defaultRange.year;

  // Monta query params opcionais: buId filtra os dados pela BU quando o backend suporta.
  const buParam: Record<string, string> = buId ? { buId } : {};
  const hasBuParam = buId != null && buId !== "";

  // Nota: ApiClient já prefixa BASE_URL "/v1" — não repetir aqui.
  const [funnelRes, forecastRes, rankingRes] = await Promise.all([
    apiClient.get<FunnelApiResponse>("/reports/funnel", hasBuParam ? buParam : undefined),
    apiClient.get<ForecastApiResponse>("/reports/forecast", { from, to, ...buParam }),
    apiClient.get<RankingApiResponse>("/reports/ranking", hasBuParam ? buParam : undefined),
  ]);

  const funnel = (funnelRes.stages ?? []).map(mapFunnelStage);
  const forecast = mapForecastRows(forecastRes.rows ?? [], year);
  const ranking = (rankingRes.rows ?? []).map(mapRankingRow);

  return { funnel, forecast, ranking };
}

/**
 * Range de período para parametrizar as queries de forecast/funil.
 * Quando omitido, usa o ano corrente como padrão.
 */
export interface ReportRange {
  readonly from: string;
  readonly to: string;
}

/**
 * Busca os dados de relatórios (funnel, forecast, ranking).
 *
 * O parâmetro opcional `range` sobrescreve o range padrão (ano corrente)
 * utilizado pelo forecast.
 * O parâmetro opcional `buId` filtra os dados pela BU quando o backend suporta.
 * Chamadas existentes sem parâmetros continuam funcionando (backward-compatible).
 */
export async function fetchReports(range?: ReportRange, buId?: string | null): Promise<ReportsData> {
  const src = await resolveSource("reports");
  return src.kind === "api" ? fetchReportsApi(range, buId) : fetchReportsMock();
}

// ---------------------------------------------------------------------------
// Funil por conta (Req 1 — extensão)
//   GET /reports/funnel-by-account
//     → { accounts: [{ accountId, accountName, ownerName, opportunityCount,
//                       totalCents, weightedForecastCents, currency,
//                       opportunities: [{ opportunityId, stageId, stageName,
//                         stageCategory, ownerId, ownerName, totalCents,
//                         weightedForecastCents, currency }] }] }
// ---------------------------------------------------------------------------

interface FunnelByAccountOpportunityDto {
  readonly opportunityId: string;
  readonly stageName: string;
  readonly stageCategory: string;
  readonly ownerName?: string | null;
  readonly totalCents: number;
  readonly currency: string;
}

interface FunnelByAccountRowDto {
  readonly accountId: string;
  readonly accountName?: string | null;
  readonly ownerName?: string | null;
  readonly opportunityCount: number;
  readonly totalCents: number;
  readonly currency: string;
  readonly opportunities: readonly FunnelByAccountOpportunityDto[];
}

interface FunnelByAccountApiResponse {
  readonly accounts: readonly FunnelByAccountRowDto[];
}

function mapFunnelByAccountOpportunity(dto: FunnelByAccountOpportunityDto): FunnelByAccountOpportunity {
  return {
    opportunityId: dto.opportunityId,
    stageName: dto.stageName,
    stageCategory: dto.stageCategory,
    ownerName: dto.ownerName?.trim() || "—",
    total: { amountCents: dto.totalCents, currency: toCurrency(dto.currency) },
  };
}

function mapFunnelByAccountRow(dto: FunnelByAccountRowDto): FunnelByAccountRow {
  return {
    accountId: dto.accountId,
    accountName: dto.accountName?.trim() || "—",
    ownerName: dto.ownerName?.trim() || "—",
    opportunityCount: dto.opportunityCount,
    total: { amountCents: dto.totalCents, currency: toCurrency(dto.currency) },
    opportunities: (dto.opportunities ?? []).map(mapFunnelByAccountOpportunity),
  };
}

async function fetchFunnelByAccountMock(): Promise<readonly FunnelByAccountRow[]> {
  const { opps, accounts, owners } = await import("../mock.js");

  const stageLabel: Record<string, string> = {
    lead: "Lead",
    qualify: "Qualificação",
    propose: "Proposta",
    negotiate: "Negociação",
    won: "Fechado · Ganho",
    lost: "Perdido",
  };

  const byAccount = new Map<string, FunnelByAccountOpportunity[]>();
  for (const opp of opps) {
    const list = byAccount.get(opp.account) ?? [];
    list.push({
      opportunityId: opp.id,
      stageName: stageLabel[opp.stage] ?? opp.stage,
      stageCategory: opp.stage,
      ownerName: owners[opp.owner]?.name ?? opp.owner,
      total: { amountCents: opp.value * 100, currency: "BRL" },
    });
    byAccount.set(opp.account, list);
  }

  const rows: FunnelByAccountRow[] = [];
  for (const [accountId, opportunities] of byAccount.entries()) {
    const sorted = [...opportunities].sort((a, b) => b.total.amountCents - a.total.amountCents);
    const account = accounts.find((a) => a.id === accountId);
    const totalCents = sorted.reduce((sum, o) => sum + o.total.amountCents, 0);
    rows.push({
      accountId,
      accountName: account?.name ?? accountId,
      ownerName: sorted[0]?.ownerName ?? "—",
      opportunityCount: sorted.length,
      total: { amountCents: totalCents, currency: "BRL" },
      opportunities: sorted,
    });
  }

  return rows.sort((a, b) => b.total.amountCents - a.total.amountCents);
}

/** Busca o relatório de funil por conta (agregado + drill-down de oportunidades). */
export async function fetchFunnelByAccount(): Promise<readonly FunnelByAccountRow[]> {
  const src = await resolveSource("reports");
  if (src.kind !== "api") {
    return fetchFunnelByAccountMock();
  }
  const res = await apiClient.get<FunnelByAccountApiResponse>("/reports/funnel-by-account");
  return (res.accounts ?? []).map(mapFunnelByAccountRow);
}

// ---------------------------------------------------------------------------
// Forecast hierárquico (Req 6 — extensão)
//   GET /reports/forecast-hierarchical?from=YYYY-MM-DD&to=YYYY-MM-DD
//     → { years: [{ year, currency, weightedForecastCents, realizedCents,
//                    goalCents?, quarters: [{ quarter, weightedForecastCents,
//                    realizedCents, goalCents?, months: [{ month,
//                    weightedForecastCents, realizedCents, goalCents? }] }] }] }
// ---------------------------------------------------------------------------

interface ForecastMonthDto {
  readonly month: number;
  readonly weightedForecastCents: number;
  readonly realizedCents: number;
  readonly goalCents?: number | null;
}

interface ForecastQuarterDto {
  readonly quarter: number;
  readonly weightedForecastCents: number;
  readonly realizedCents: number;
  readonly goalCents?: number | null;
  readonly months: readonly ForecastMonthDto[];
}

interface ForecastYearDto {
  readonly year: number;
  readonly currency: string;
  readonly weightedForecastCents: number;
  readonly realizedCents: number;
  readonly goalCents?: number | null;
  readonly quarters: readonly ForecastQuarterDto[];
}

interface ForecastHierarchyApiResponse {
  readonly years: readonly ForecastYearDto[];
}

function goalMoney(goalCents: number | null | undefined, currency: Money["currency"]): Money | null {
  return goalCents === null || goalCents === undefined ? null : { amountCents: goalCents, currency };
}

function mapForecastMonth(dto: ForecastMonthDto, currency: Money["currency"]): ForecastMonthNode {
  return {
    month: dto.month,
    forecast: { amountCents: dto.weightedForecastCents, currency },
    realized: { amountCents: dto.realizedCents, currency },
    goal: goalMoney(dto.goalCents, currency),
  };
}

function mapForecastQuarter(dto: ForecastQuarterDto, currency: Money["currency"]): ForecastQuarterNode {
  return {
    quarter: dto.quarter,
    forecast: { amountCents: dto.weightedForecastCents, currency },
    realized: { amountCents: dto.realizedCents, currency },
    goal: goalMoney(dto.goalCents, currency),
    months: (dto.months ?? []).map((m) => mapForecastMonth(m, currency)),
  };
}

function mapForecastYear(dto: ForecastYearDto): ForecastYearNode {
  const currency = toCurrency(dto.currency);
  return {
    year: dto.year,
    currency,
    forecast: { amountCents: dto.weightedForecastCents, currency },
    realized: { amountCents: dto.realizedCents, currency },
    goal: goalMoney(dto.goalCents, currency),
    quarters: (dto.quarters ?? []).map((q) => mapForecastQuarter(q, currency)),
  };
}

async function fetchForecastHierarchyMock(): Promise<readonly ForecastYearNode[]> {
  const year = new Date().getFullYear();
  // Forecast/realizado fictícios por mês (centavos, BRL) — exercita expand/collapse.
  const monthly = Array.from({ length: 12 }, (_, i) => ({
    month: i + 1,
    forecastCents: (i + 1) * 1_000_00,
    realizedCents: i % 2 === 0 ? (i + 1) * 400_00 : 0,
    goalCents: (i + 1) * 1_200_00,
  }));

  const quarters: ForecastQuarterNode[] = [1, 2, 3, 4].map((quarter) => {
    const months = monthly
      .filter((m) => Math.ceil(m.month / 3) === quarter)
      .map<ForecastMonthNode>((m) => ({
        month: m.month,
        forecast: { amountCents: m.forecastCents, currency: "BRL" },
        realized: { amountCents: m.realizedCents, currency: "BRL" },
        goal: { amountCents: m.goalCents, currency: "BRL" },
      }));
    return {
      quarter,
      forecast: { amountCents: months.reduce((s, m) => s + m.forecast.amountCents, 0), currency: "BRL" },
      realized: { amountCents: months.reduce((s, m) => s + m.realized.amountCents, 0), currency: "BRL" },
      goal: { amountCents: months.reduce((s, m) => s + (m.goal?.amountCents ?? 0), 0), currency: "BRL" },
      months,
    };
  });

  return [
    {
      year,
      currency: "BRL",
      forecast: { amountCents: quarters.reduce((s, q) => s + q.forecast.amountCents, 0), currency: "BRL" },
      realized: { amountCents: quarters.reduce((s, q) => s + q.realized.amountCents, 0), currency: "BRL" },
      goal: { amountCents: quarters.reduce((s, q) => s + (q.goal?.amountCents ?? 0), 0), currency: "BRL" },
      quarters,
    },
  ];
}

/** Busca o forecast hierárquico (mês → trimestre → ano), por moeda. */
export async function fetchForecastHierarchy(range?: ReportRange): Promise<readonly ForecastYearNode[]> {
  const src = await resolveSource("reports");
  if (src.kind !== "api") {
    return fetchForecastHierarchyMock();
  }
  const defaultRange = currentYearForecastRange();
  const from = range?.from ?? defaultRange.from;
  const to = range?.to ?? defaultRange.to;
  const res = await apiClient.get<ForecastHierarchyApiResponse>("/reports/forecast-hierarchical", { from, to });
  return (res.years ?? []).map(mapForecastYear);
}

// ---------------------------------------------------------------------------
// Export CSV
// ---------------------------------------------------------------------------

export interface ExportParams {
  readonly type: "funnel" | "funnel-by-account" | "forecast" | "ranking" | "commissions";
  readonly buId?: string;
  readonly period?: string;
}

/**
 * DTO retornado por GET /reports/{type}/export.
 *
 * Shape confirmado via curl (Content-Type: application/json):
 *   {
 *     "reportType": "funnel",
 *     "filename": "funnel_2026-01_2026-12.csv",
 *     "signedUrl": "https://storage.googleapis.com/...",
 *     "expiresAt": "2026-06-17T13:55:01.18Z"
 *   }
 *
 * O download ocorre abrindo a signedUrl diretamente (GCS pré-assinado, expira em ~15 min).
 */
interface ExportCsvDto {
  readonly reportType: string;
  readonly filename: string;
  readonly signedUrl: string;
  readonly expiresAt: string;
}

/**
 * Solicita exportação CSV ao backend (GET /reports/{type}/export) e retorna
 * a URL assinada e o nome do arquivo sugerido pelo backend.
 *
 * A camada de apresentação (feature/hook) é responsável por disparar o download
 * via DOM, mantendo a separação de camadas (seção 3.1 do design, ST-01).
 *
 * Parâmetros opcionais buId e period são enviados como query params quando presentes.
 */
export async function exportReportCsv(params: ExportParams): Promise<{ signedUrl: string; filename: string }> {
  const queryParams: Record<string, string> = {};
  if (params.buId !== undefined) queryParams["buId"] = params.buId;
  if (params.period !== undefined) queryParams["period"] = params.period;

  const dto = await apiClient.get<ExportCsvDto>(
    `/reports/${params.type}/export`,
    Object.keys(queryParams).length > 0 ? queryParams : undefined,
  );

  // Valida que a URL é https:// antes de retorná-la, prevenindo que uma
  // signedUrl malformada (ex.: javascript:, data:, http:) provoque navegação
  // ou execução de código quando o caller usar o anchor (PLAUSIBLE, LOW).
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(dto.signedUrl);
  } catch {
    throw new Error(`exportReportCsv: signedUrl inválida recebida do backend`);
  }
  if (parsedUrl.protocol !== "https:") {
    throw new Error(
      `exportReportCsv: signedUrl com protocolo não permitido: "${parsedUrl.protocol}"`,
    );
  }

  return { signedUrl: dto.signedUrl, filename: dto.filename };
}
